import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { portalServer } from "../config";
import { SyncModule, useSyncData, useSyncModuleRegistration } from "../sync";
import {
  KartenObjekt,
  KartenObjektAdresse,
  KartenObjektLink,
  KartenObjektNotiz,
  RisikoKlasse,
} from "../types";
import { limiter } from "./demo";
import { usePortalApi } from "./portal-api";
import { daily } from "./provider";

const syncModuleId = "kartenobjekte";

function useKartenobjekteSyncModule(): SyncModule<ReadonlyArray<KartenObjekt>> {
  const { portalKey, demo } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `karte:tasks.${syncModuleId}`,
      defaultOptions: {
        schedule: { type: "periodic", interval: daily },
      },
      fetchData: useNewApi && baseUrl
        ? async () =>
            limiter<KartenObjekt>(demo, 20)(
              await fetchKartenobjekteNew(portalKey, baseUrl),
            )
        : async () => {
            const response = await fetch(
              `${portalServer}/get-objects-v2.php?ORG_ACCESSKEY=${encodeURIComponent(
                portalKey,
              )}`,
            );

            if (!response.ok) {
              throw new Error(
                `Error fetching data: ${response.status} ${response.statusText}`,
              );
            }

            const data = await response.json();
            const limited = limiter<KartenObjekt>(demo, 20);
            return limited(data.OBJEKT_LIST ?? []).map(parseKartenObjektLegacy);
          },
    }),
    [demo, portalKey, useNewApi, baseUrl],
  );
}

async function fetchKartenobjekteNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<KartenObjekt>> {
  const response = await fetch(`${baseUrl}/api/device/sync/objects`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.objects ?? [])
    .filter((o: any) => o.status === "ACTIVE" && o.latitude != null && o.longitude != null)
    .map(parseKartenObjektNew);
}

function parseKartenObjektNew(entry: any): KartenObjekt {
  // The object's own Dokumente-Ordner takes the primary link slot (same
  // convention as the legacy `OBJEKT_LINK: "folder:<id>"` string), so the
  // existing folder button/inline browser (useDokumentLink, ObjektDetails)
  // keeps working unchanged. Zusatzordner/Zusatzlinks (additionalLinks) are
  // a separate, arbitrarily long list — never squeezed into that one slot.
  const link = entry.folderId ? `folder:${entry.folderId}` : undefined;

  return {
    id: entry.id,
    typ: "objekt",
    icon: parseObjektIconNew(entry.customIcon),
    latitude: entry.latitude,
    longitude: entry.longitude,
    name: entry.name,
    aliase: entry.aliasNames || undefined,
    beschreibung: entry.shortDescription || undefined,
    adresse: entry.address || undefined,
    einsatzplan: entry.fwepNumber || undefined,
    interneNummer: entry.internalNumber || undefined,
    risikoKlasse: parseRisikoKlasseNew(entry.riskLevel),
    gelaende: entry.gelaende || undefined,
    objektart: entry.objektart || undefined,
    objektnutzung: entry.objektnutzung || undefined,
    betriebszeiten: entry.betriebszeiten || undefined,
    anwesendePersonen: entry.anwesendePersonen || undefined,
    zielklinik: entry.zielklinik || undefined,
    bmz: entry.bmz || undefined,
    fsd: entry.fsd || undefined,
    fwaufzug: entry.fwaufzug || undefined,
    gebaeudefunk: entry.gebaeudefunk || undefined,
    gefahrenhinweis: entry.gefahrenhinweis || undefined,
    spz: entry.spz || undefined,
    link,
    links: parseZusatzlinksNew(entry.additionalLinks),
    adressen: parseObjektAdressenNew(entry.addresses),
    notizen: parseObjektNotizenNew(entry.notes),
    owned: !entry.shared,
  };
}

// New API: entries can carry an arbitrary custom icon independent of `typ`
// (e.g. "icon_we_h_we.png"), already matching a @rescuetablet/map-icons key
// once the extension is stripped. Falls back to the "objekt" icon (from
// `typ` above) when absent — same convention as the water supply points.
function parseObjektIconNew(customIcon?: string | null): string | undefined {
  if (!customIcon) return undefined;
  return customIcon.replace(/\.[^.]+$/, "");
}

function parseZusatzlinksNew(
  additionalLinks: any[],
): ReadonlyArray<KartenObjektLink> | undefined {
  if (!additionalLinks?.length) return undefined;

  const links = additionalLinks
    .map((l: any): KartenObjektLink | undefined => {
      if (l.linkType === "WEBLINK" && l.webLink)
        return { id: l.id, url: l.webLink, label: l.label || undefined };
      if (l.linkType === "FOLDER" && l.folderId)
        return {
          id: l.id,
          url: `folder:${l.folderId}`,
          label: l.label || undefined,
        };
      if (l.linkType === "DOCUMENT" && l.documentId)
        return {
          id: l.id,
          url: `file:${l.documentId}`,
          label: l.label || undefined,
        };
      return undefined;
    })
    .filter((l: KartenObjektLink | undefined): l is KartenObjektLink => !!l);

  return links.length > 0 ? links : undefined;
}

function parseObjektAdressenNew(
  addresses: any[],
): ReadonlyArray<KartenObjektAdresse> | undefined {
  if (!addresses?.length) return undefined;
  return addresses.map((adresse: any) => ({
    id: adresse.id,
    label: adresse.label || undefined,
    adresse: adresse.address,
    latitude: adresse.latitude ?? undefined,
    longitude: adresse.longitude ?? undefined,
    icon: parseObjektIconNew(adresse.icon),
  }));
}

function parseObjektNotizenNew(
  notes: any[],
): ReadonlyArray<KartenObjektNotiz> | undefined {
  if (!notes?.length) return undefined;
  return notes.map((notiz: any) => ({
    id: notiz.id,
    titel: notiz.title,
    beschreibung: notiz.description || undefined,
    istGefahr: notiz.isHazard || undefined,
  }));
}

const risikoKlasseNewMap: Record<string, RisikoKlasse> = {
  LOW: "niedrig",
  MEDIUM: "mittel",
  HIGH: "hoch",
};

function parseRisikoKlasseNew(level?: string): RisikoKlasse | undefined {
  if (!level || level === "NONE") return undefined;
  return risikoKlasseNewMap[level];
}

export function useKartenobjekteSync() {
  const module = useKartenobjekteSyncModule();
  useSyncModuleRegistration(module);
}

export function useKartenObjekte(): ReadonlyArray<KartenObjekt> {
  return useSyncData(syncModuleId) ?? [];
}

function parseKartenObjektLegacy(entry: any): KartenObjekt {
  return {
    id: entry.OBJEKT_ID,
    typ: "objekt",
    latitude: parseFloat(entry.OBJEKT_GEO_LAT),
    longitude: parseFloat(entry.OBJEKT_GEO_LNG),
    name: entry.OBJEKT_NAME,
    aliase: entry.OBJEKT_ALIASE || undefined,
    beschreibung: entry.OBJEKT_DESCRIPTION || undefined,
    link: entry.OBJEKT_LINK || undefined,
    adresse: entry.OBJEKT_ADRESSE || undefined,
    einsatzplan: entry.OBJEKT_NR_FWEP || undefined,
    interneNummer: entry.OBJEKT_NR_INTERNAL || undefined,
    risikoKlasse: parseRisikoKlasseLegacy(entry.OBJEKT_RISIKO),
    owned: entry.OBJEKT_OWN,
    links: parseZusatzlinkLegacy(entry),
  };
}

const risikoKlassenLegacy: RisikoKlasse[] = ["keine", "niedrig", "mittel", "hoch"];

function parseRisikoKlasseLegacy(id?: string): RisikoKlasse | undefined {
  if (!id || id === "0") return undefined;
  return risikoKlassenLegacy[parseInt(id, 10)];
}

function parseZusatzlinkLegacy(
  entry: any,
): ReadonlyArray<KartenObjektLink> | undefined {
  const url = entry.OBJEKT_LINK2;
  if (!url) return undefined;
  return [{ url, label: entry.OBJEKT_LINK2DESC || undefined }];
}
